import { queryOptions, useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { privateApi } from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import { AccountResponse } from '@/types/api';
import { handleAccountForStore } from '@/utils/handle-store-data';

export const fetchAccount = async (
  apiKey?: string,
): Promise<AccountResponse> => {
  return privateApi.get<AccountResponse>('/account', {
    params: {},
    ...(apiKey
      ? {
          headers: {
            'X-API-KEY': apiKey,
          },
        }
      : {}),
  });
};

export const getAccountQueryOptions = (
  address?: string,
  apiKeyOverride?: string,
) => {
  const apiKey =
    apiKeyOverride ?? useAccountStore.getState().getAuthParams(address)?.apiKey;
  return queryOptions({
    queryKey: ['account', address, apiKey], // Use apiKey instead of signingKey
    queryFn: () =>
      fetchAccount(apiKey).then((res) => {
        handleAccountForStore(res, address);
        return res;
      }),
  });
};

type UseFetchAccountOptions = {
  address?: string | null;
  disabled?: boolean;
};

export const useFetchAccount = ({
  address: forcedAddress,
  disabled,
}: UseFetchAccountOptions = {}) => {
  const {
    address: storeAddress,
    hasAuth,
    apiKey,
    accGroup,
  } = useAccountStore(
    useShallow((state) => {
      const authParams = state.getAuthParams(forcedAddress ?? state.address);
      return {
        address: state.address,
        hasAuth: state.hasAuth,
        apiKey: authParams?.apiKey,
        accGroup: authParams?.accountGroup,
      };
    }),
  );

  const address = forcedAddress ?? storeAddress;

  const result = useQuery({
    enabled:
      !disabled &&
      hasAuth &&
      !!address &&
      apiKey !== undefined &&
      accGroup !== 2,
    staleTime: 0,
    refetchOnWindowFocus: false,
    ...getAccountQueryOptions(address, apiKey),
  });

  return result;
};
